import { differenceInCalendarDays } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { Share } from 'react-native';
import { useShallow } from 'zustand/shallow';

import { showAlert, showConfirm } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { InvitationSentInfo } from '@/features/guardian-player/types';
import { formatGuardianApiDate, parseGuardianApiDate } from '@/features/guardian-player/utils';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { useGuardianStackNavigation } from '@/hooks';
import { useInvitePlayerGuardianMutation } from '@/queries/use-invite-player-guardian-mutation';
import {
  useInvitationPreviewQuery,
  useRemovePlayerInvitationMutation,
  useRevokePlayerInvitationMutation,
} from '@/queries/use-player-invitations-query';
import { PlayerGuardianStatus } from '@/services/get-player-guardians';
import { DATE_FORMATS } from '@/utils/date-formats';
import { useUiStore } from '@/stores';

const strings = STRINGS.guardianPlayer.invitationSent;
const circleStrings = STRINGS.guardianPlayer.guardianCircle;

function resolveRelationshipLabel(relationship?: string): string {
  const key = (relationship ?? '').toLowerCase();
  const known = (circleStrings.relationships as Record<string, string>)[key];
  if (known) return known;
  if (!relationship) return circleStrings.relationships.other;
  return relationship.charAt(0).toUpperCase() + relationship.slice(1);
}

/**
 * View model + actions for the Invitation Details screen (Waiting/Expired/Declined).
 * Owns the invitation's local status (so Revoke/Resend can flip it in place without
 * waiting on a circle refetch), the invitation preview (URL + original message, for
 * Share/Resend), and the Revoke/Resend/Remove/Share handlers.
 */
export function useInvitationSent(player: GuardianPlayer, invitation: InvitationSentInfo) {
  const navigation = useGuardianStackNavigation();
  const firstName = player.firstname?.trim() || '';

  const [status, setStatus] = useState(invitation.status);

  const { data: previewData, isLoading: isLoadingPreview } = useInvitationPreviewQuery(
    invitation.invitationCode,
  );
  const { mutateAsync: revokeInvitation } = useRevokePlayerInvitationMutation(player.id);
  const { mutateAsync: removeInvitation } = useRemovePlayerInvitationMutation(player.id);
  const { mutateAsync: invitePlayerGuardian } = useInvitePlayerGuardianMutation(player.id);
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const relationshipLabel = resolveRelationshipLabel(invitation.relationship);

  const expiresLabel = useMemo(() => {
    const expiresAt = invitation.expiresAt || previewData?.expiresAt;
    if (!expiresAt) {
      return '—';
    }
    const date = parseGuardianApiDate(expiresAt);
    if (!date) return '';
    const dateText = formatGuardianApiDate(expiresAt, DATE_FORMATS.TEXT);
    if (status === PlayerGuardianStatus.Expired) return dateText;
    const daysLeft = differenceInCalendarDays(date, new Date());
    return daysLeft > 0 ? strings.expiresCountdown(daysLeft, dateText) : dateText;
  }, [invitation.expiresAt, status, previewData?.expiresAt]);

  const handleShareLink = useCallback(() => {
    const inviteUrl = previewData?.inviteUrl || invitation.inviteUrl || '';

    Share.share({
      message: `${strings.shareMessage.replace('{name}', firstName)}\n\n${inviteUrl}`.trim(),
    }).catch(() => {
      // User dismissed the sheet or sharing is unavailable — nothing to do.
    });
  }, [firstName, previewData?.inviteUrl, invitation.inviteUrl]);

  const handleRevoke = useCallback(async () => {
    // After sending, the invite response gives `id` but not `invitationCode`; from the
    // circle both are present. Fall back to `id` so revoke works from either entry point.
    const invitationRef = invitation.invitationCode || invitation.id;
    if (!invitationRef) return;

    const confirmed = await showConfirm({
      title: strings.revokeConfirmTitle,
      message: strings.revokeConfirmMessage,
      confirmText: strings.revokeConfirm,
      cancelText: strings.revokeCancel,
      confirmStyle: 'destructive',
    });
    if (!confirmed) return;

    try {
      showGlobalLoading();
      await revokeInvitation(invitationRef);
      setStatus(PlayerGuardianStatus.Expired);
    } catch {
      showAlert({ title: strings.revokeErrorTitle, message: strings.revokeErrorMessage });
    } finally {
      hideGlobalLoading();
    }
  }, [
    invitation.invitationCode,
    invitation.id,
    revokeInvitation,
    showGlobalLoading,
    hideGlobalLoading,
  ]);

  const handleResend = useCallback(async () => {
    // Preview may not have loaded yet (e.g. straight after inviting, before its token query
    // resolves) — fall back to the email we already carry from the invite form. Both are the
    // recipient's email, so this isn't an ambiguous fallback.
    const invitedEmail = previewData?.invitedEmail || invitation.email || '';
    if (!invitedEmail) {
      showAlert({ title: strings.resendErrorTitle, message: strings.resendErrorMessage });
      return;
    }

    const confirmed = await showConfirm({
      title: strings.resendConfirmTitle,
      message: strings.resendConfirmMessage,
      confirmText: strings.resendConfirm,
      cancelText: strings.resendCancel,
    });
    if (!confirmed) return;

    try {
      showGlobalLoading();
      await invitePlayerGuardian({
        invitedEmail,
        relationship: invitation.relationship || circleStrings.relationships.other,
        // Carry over the original personal note (preview first, else the note we sent) so
        // resending doesn't drop it.
        message: previewData?.message || invitation.message || undefined,
        refresh: true,
      });
      setStatus(PlayerGuardianStatus.Waiting);
    } catch {
      showAlert({ title: strings.resendErrorTitle, message: strings.resendErrorMessage });
    } finally {
      hideGlobalLoading();
    }
  }, [
    previewData?.invitedEmail,
    invitation.email,
    invitation.relationship,
    invitation.message,
    invitePlayerGuardian,
    previewData?.message,
    showGlobalLoading,
    hideGlobalLoading,
  ]);

  // No confirmation per BR-02 — removes immediately and returns to the circle. Uses the
  // remove mutation (not revoke): the invitation is already dead (expired/declined) so the
  // server deletes it outright — we only refresh the circle list and never refetch the gone
  // invitation by token (that would 404). Also covers the Declined "Remove" action.
  const handleRemove = useCallback(async () => {
    if (!invitation.id) return;

    try {
      showGlobalLoading();
      await removeInvitation(invitation.id);
      navigation.goBack();
    } catch {
      showAlert({ title: strings.removeErrorTitle, message: strings.removeErrorMessage });
    } finally {
      hideGlobalLoading();
    }
  }, [invitation.id, removeInvitation, navigation, showGlobalLoading, hideGlobalLoading]);

  return {
    firstName,
    status,
    expiresLabel,
    relationshipLabel,
    onBack: navigation.goBack,
    onBackToCircle: navigation.goBack,
    onRevoke: handleRevoke,
    onResend: handleResend,
    onRemove: handleRemove,
    onShareLink: handleShareLink,
    isShareLoading: isLoadingPreview,
  };
}
